/**
 * Contact-request domain — utility types + non-hook helpers.
 *
 * Holds the imperative (non-hook) fetch/create helpers that wrap the
 * generated client functions, plus the domain types that augment what
 * drf-spectacular can't express (the `{id,label}` status method-field and
 * the custom `total_credits_used` aggregate on the list response).
 *
 * Mutations that ARE hooks live in `hooks.ts` (they go through the
 * invalidation registry). These imperative helpers exist because their
 * consumers call them inside `useQuery`/`useEffect` bodies or one-off click
 * handlers, mirroring the old hand-written `contactRequestApi.ts` surface so
 * the migration is an import-path swap.
 */

import {
  ContactRequestJobStatus,
  type ContactRequestJobResponse,
  type RequestedContactResponse,
} from 'models/contact_request'
import { CompanyTypeSlug } from '../../models/companies'
import { adaptUnifiedListParams } from '../opportunities/unifiedResponseAdapter'
import { tryGetTargetType } from '../opportunities/targetTypeMap'

import { checkContactRequestStatus as checkContactRequestStatusClient } from '../generated/clients/checkContactRequestStatus'
import { createChainContactRequestJob } from '../generated/clients/createChainContactRequestJob'
import { createCnuContactRequestJob } from '../generated/clients/createCnuContactRequestJob'
import { createK12ContactRequestJob } from '../generated/clients/createK12ContactRequestJob'
import { createUgcContactRequestJob } from '../generated/clients/createUgcContactRequestJob'
import { getContactRequestJobDetail } from '../generated/clients/getContactRequestJobDetail'
import { getCreditBalance } from '../generated/clients/getCreditBalance'
import { listContactRequestJobs } from '../generated/clients/listContactRequestJobs'
import { listRequestedContacts } from '../generated/clients/listRequestedContacts'

export { ContactRequestJobStatus }

// ── Request/response types (kept stable for consumers) ─────────────────────--

export type ContactRequestJobCompanyType = 'chain' | 'k12' | 'cnu' | 'ugc'

export type CreateContactRequestJobRequest = {
  chain_proxy_ids?: Array<number>
  exclude_chain_proxy_ids?: Array<number>
  max_contacts: number
  sub_category_slug: CompanyTypeSlug
}

export type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: Array<T>
}

/**
 * Paginated job list. drf-spectacular sees neither the custom
 * `total_credits_used` aggregate the view appends nor the `{id,label}` status
 * shape, so both are re-added on top of the model response type.
 */
export type ContactRequestJobPaginatedResponse =
  PaginatedResponse<ContactRequestJobResponse> & {
    total_credits_used: number
  }

export type ContactRequestJobDetailResponse = ContactRequestJobResponse & {
  requested_contacts: RequestedContactResponse[]
}

export type SubcategoryInfo = {
  slug: string
  name: string
  company_count: number
  contact_request_allowed: boolean
}

export type ContactStatusResponse = {
  status: 'in_progress' | 'found_contacts' | null
  message: string
  subcategories?: SubcategoryInfo[]
  companies_in_progress?: Record<string, any>
  companies_with_contacts?: Record<string, any>
  has_companies_in_progress?: boolean
  has_companies_with_contacts?: boolean
  campaign_companies?: {
    results: Array<{
      id: number
      name: string
      status: string
    }>
    total_count: number
  }
  credits?: {
    total: number
    on_hold: number
    available: number
  }
}

export type CreditTransaction = {
  id: string
  created: number
  amount: number
  memo: string
  status: string
  type: string
  expires_at?: number
  metadata?: Record<string, string>
}

export type CreditBalanceResponse = {
  credits: {
    total: number
    available: number
    used: number
    pending: number
    on_hold: number
  }
  transactions: CreditTransaction[]
}

export type CreateCheckoutSessionRequest = {
  amount: number
  success_url: string
  cancel_url: string
}

export type CreateCheckoutSessionResponse = {
  session_id: string
  url: string
}

// ── Status helper ──────────────────────────────────────────────────────────-

/**
 * A contact request job is "pending" when it has been submitted but not yet
 * completed, rejected, or cancelled. The list serializer collapses backend
 * statuses so every pending stage sorts before `ACCEPTED`.
 */
export function isPendingContactRequestStatus(statusId: number): boolean {
  return statusId < ContactRequestJobStatus.ACCEPTED
}

// ── Imperative client wrappers ─────────────────────────────────────────────-

/**
 * Create a contact request job for the company type matching `companyTypeSlug`.
 *
 * Dispatches to the per-type generated client (chain/k12/cnu/ugc) and forwards
 * `params` as query params so the backend can resolve the target queryset when
 * `chain_proxy_ids` is omitted (select-all). Imperative (not a hook) because the
 * caller picks the endpoint at submit time; hooks can't be called conditionally.
 *
 * FIR-7878: for opportunities verticals (restaurant/university/k12) the select-all
 * set is resolved server-side through the unified `/opportunities/` engine, so the
 * query params must match what the unified table list sends — the same
 * `adaptUnifiedListParams` renames + a `target_type`. UGC (non-opp) keeps its raw
 * per-vertical params.
 */
export async function createContactRequestJob(
  companyTypeSlug: CompanyTypeSlug,
  body: CreateContactRequestJobRequest,
  params: Record<string, any>
) {
  const client = (() => {
    switch (companyTypeSlug) {
      case 'restaurants-bars':
        return createChainContactRequestJob
      case 'education-cu':
        return createCnuContactRequestJob
      case 'education-k-12':
        return createK12ContactRequestJob
      default:
        return createUgcContactRequestJob
    }
  })()

  const targetType = tryGetTargetType(companyTypeSlug)
  const resolvedParams = targetType
    ? { ...adaptUnifiedListParams(params, targetType), target_type: targetType }
    : params

  try {
    return await client(body, { params: resolvedParams })
  } catch (error: any) {
    // Surface insufficient-credits with the fields the backend actually sends
    // ({ error, current_balance, required_credits }).
    if (error.response?.data?.error === 'Insufficient credits') {
      throw {
        message: error.response.data.error,
        requiredCredits: error.response.data.required_credits,
        currentBalance: error.response.data.current_balance,
      }
    }
    throw error
  }
}

export async function fetchContactRequestJobs(
  params: Record<string, any> = {},
  signal?: AbortSignal
): Promise<ContactRequestJobPaginatedResponse> {
  return (await listContactRequestJobs(params, {
    signal,
  })) as unknown as ContactRequestJobPaginatedResponse
}

export async function fetchContactRequestJobDetails(
  jobId: number
): Promise<ContactRequestJobDetailResponse> {
  return (await getContactRequestJobDetail({
    id: jobId,
  })) as unknown as ContactRequestJobDetailResponse
}

export async function fetchRequestedContacts(
  jobId: number,
  params: Record<string, string | number> = {},
  signal?: AbortSignal
): Promise<PaginatedResponse<RequestedContactResponse>> {
  return (await listRequestedContacts({ job_id: jobId }, params, {
    signal,
  })) as unknown as PaginatedResponse<RequestedContactResponse>
}

export async function checkContactRequestStatus(
  campaignId: number
): Promise<ContactStatusResponse> {
  return (await checkContactRequestStatusClient({
    campaign_id: campaignId,
  })) as unknown as ContactStatusResponse
}

export async function fetchCreditBalance(): Promise<CreditBalanceResponse> {
  return (await getCreditBalance()) as unknown as CreditBalanceResponse
}
